<script>
    import {binaryDataUrl, binaryMetadataUrl, propSoundsUrl, propsUrl} from "../urls";
    import {customFetch} from "../customFetch";
    import {notifications} from "../notifications";
    import {dateFormatter} from "../dateFormatter";
    import FbxViewer from "../components/FbxViewer.svelte";
    import StraightTable from "../components/StraightTable.svelte";
    import {binaryView, dropdownView, valueView, saveRows} from "../tableUtils";

    const checkboxClass = "";

    let searchFields = ["Name", "Collection", "InternalKey", "Bundle", "TagsString"]
    let savingFields = [
        "ID",
        "Name",
        "Width",
        "Height",
        "Tallness",
        "SnapToGrid",
        "Permanent",
        "DoNotBlockTracks",
        "AreaMultiplier",
        "DestroyFee",
        "InternalKey",
        "Collection",
    ]

    let currentRow = -1;

    function highlightClass(row, indexes, diff) {
        if (multiEditIndexes[row.ID]) return "selected";

        if (diff[row.ID]) return "changed";

        return "";
    }

    let multiEditIndexes = {};

    function multiEdit(field, row) {
        let index = props.indexOf(row);
        props[index] = row;
        if (!multiEditIndexes[row.ID]) return;

        let value = row[field];
        for (let i = 0; i < props.length; i++) {
            let prop = props[i];
            let id = prop.ID;

            if (multiEditIndexes[id] === true) {
                props[i][field] = value;
            }
        }
    }

    let props = [];
    let originalProps = {};

    let diff = {};

    $: {
        let result = {};

        for (let i = 0; i < props.length; i++) {
            let prop = props[i];
            let original = originalProps[prop.ID];

            for (let field of savingFields) {
                if (original && original[field] !== prop[field]) {
                    result[props[i].ID] = true;
                }
            }
        }

        diff = result;
    }

    async function loadProps() {
        try {
            let response = await customFetch(propsUrl());

            let data = await response.json();
            for (let i = 0; i < data.length; i++) {
                data[i].UpdatedAt = new Date(data[i].UpdatedAt)
                data[i].Bundle = data[i].Tags[0]
                data[i].TagsString = data[i].Tags.slice(1).join(", ")
            }

            originalProps = {};
            for (let i = 0; i < data.length; i++) {
                originalProps[data[i].ID] = Object.assign({}, data[i])
            }
            props = data;
        } catch (e) {
            notifications.danger(
                `Can't get actual props list from server: ${e.message}`
            );
        }
    }

    async function uploadProps() {
        const parcel = [];
        for (let i = 0; i < props.length; i++) {
            if (diff[props[i].ID]) {
                let item = {};
                for (let j = 0; j < savingFields.length; j++) {
                    let field = savingFields[j];
                    item[field] = props[i][field];
                    if (field === "Name" && originalProps[field] !== item[field]) {
                        item.NameOverride = true
                    }
                }
                parcel.push(item);
            }
        }

        let request = {
            method: "PATCH",
            body: JSON.stringify(parcel),
        };

        try {
            await customFetch(propsUrl(), request);

            await loadProps();

            notifications.info("Successfully saved")
        } catch (e) {
            notifications.danger(
                `Can't upload props: ${e.message}`
            );
        }
    }

    let searchValue = "";

    let filteredRows;

    let filterTypes = {
        all: "all",
        last24Hours: "today"
    };

    function sortByColumn(column) {
        if (sortBy.column === column)
            sortBy.ascending = !sortBy.ascending;
        else {
            sortBy.column = column;
            sortBy.ascending = true;
        }
    }

    let sortBy = {column: "Name", ascending: true};

    let sortedRows = [];
    $: {
        let multiplier = (sortBy.ascending) ? 1 : -1;
        let column = sortBy.column;

        sortedRows = props.sort((a, b) => {
            if (a[column] < b[column])
                return -1 * multiplier;
            if (a[column] > b[column])
                return 1 * multiplier;

            return 0;
        })
    }

    $: {
        filteredRows = [];

        let dayAgo = new Date();
        dayAgo.setDate(dayAgo.getDate() - 1);

        let searchLowerCase = searchValue.toLowerCase();

        for (let i = 0; i < sortedRows.length; i++) {
            let prop = sortedRows[i];

            let included = true;
            if (filterType === filterTypes.last24Hours && prop.UpdatedAt < dayAgo)
                included = false;

            let found = false;
            for (let fieldIndex = 0; fieldIndex < searchFields.length; fieldIndex++){
                let value = prop[searchFields[fieldIndex]];
                if (!value) continue;

                if (value.toLowerCase().search(searchLowerCase) !== -1){
                    found = true;
                    break;
                }
            }

            if (!found)
                included = false;

            if (included)
                filteredRows.push(sortedRows[i]);
        }
    }

    let sounds = []
    async function loadSounds(selectedValue) {
        if (selectedValue) {
            let data = await customFetch(propSoundsUrl(selectedValue.ID));
            sounds = await data.json();
        } else {
            sounds = [];
        }
    }
    $: {
        selectedValue && loadSounds(selectedValue);
    }

    let oldName = ""
    async function loadOldName(selectedValue) {
        if (selectedValue) {
            let data = await customFetch(binaryMetadataUrl(selectedValue.ModelDataId));
            let metadata = JSON.parse(await data.text());
            oldName = metadata.Filename;
        } else {
            oldName = ""
        }
    }
    $: {
        selectedValue && loadOldName(selectedValue);
    }
    let filterType = filterTypes.all;

    function setFilterType(t) {
        if (filterType !== t) {
            filterType = t;
            multiEditIndexes = {};
        }
    }

    loadProps();

    let multiEditCount;
    $: {
        multiEditCount = Object.values(multiEditIndexes).filter(value => value === true).length;
    }

    let multiEditCheckbox = null;

    $: {
        if (multiEditCheckbox) {
            let selected = [];
            for (const key in multiEditIndexes) {
                if (multiEditIndexes[key])
                    selected.push(key)
            }

            let filteredCount = 0;
            for (let i = 0; i < filteredRows.length; i++) {
                if (selected.indexOf(filteredRows[i].ID) > -1)
                    filteredCount++;
            }

            multiEditCheckbox.indeterminate = selected.length > 0 && (filteredCount !== filteredRows.length || filteredCount !== selected.length);
            multiEditCheckbox.checked = filteredRows.length === filteredCount;
        }
    }

    function toggleMultiEditMode() {
        multiEditIndexes = {};
        if (!multiEditCount > 0) {
            for (let i = 0; i < filteredRows.length; i++) {
                multiEditIndexes[filteredRows[i].ID] = true;
            }
        }
    }

    let lastClicked = null;

    function toggleMultiEditRow(row) {
        multiEditIndexes[row.ID] = !multiEditIndexes[row.ID]

        let foundLastClicked = false;
        let foundCurrent = false;
        let isSelecting = false;
        if (isShiftPressed && lastClicked && multiEditIndexes[lastClicked.ID]) {
            for (let i = 0; i < filteredRows.length; i++) {
                if (filteredRows[i] === lastClicked){
                    isSelecting = !foundCurrent;
                    foundLastClicked = true;
                }

                if (filteredRows[i] === row){
                    isSelecting = !foundLastClicked;
                    foundCurrent = true;
                }

                if (isSelecting)
                    multiEditIndexes[filteredRows[i].ID] = true;
            }
        }

        if (multiEditIndexes[row.ID])
            lastClicked = row;
    }

    function getArrow(sortBy) {
        return sortBy.ascending ? "&#9651;" : '&#9661;'; //arrow symbols
    }

    let isShiftPressed = false;

    function onKeyDown(e) {
        switch (e.keyCode) {
            case 16:
                isShiftPressed = true;
                break;
        }
    }

    function onKeyUp(e) {
        switch (e.keyCode){
            case 16:
                isShiftPressed = false;
                lastClicked = null;
                break;
        }
    }

    let selectedValue = null;
    function selectRow(value) {
        selectedValue = value;
    }
    function handleClose() {
        if (selectedValue.Name) {
            let index = props.findIndex((x) => x.ID === selectedValue.ID);
            props[index].Name = selectedValue.Name;
            selectedValue = null;
        }
    }

    async function saveSounds(e) {
        const rows = e.detail.data;
        let fields = soundColumns.map(elem => elem.field);
        await saveRows(rows, fields, propSoundsUrl(selectedValue.ID));
        await loadSounds(selectedValue)
    }

    let soundTypes = {Distant: 'Distant', Close: 'Close', Detail: 'Detail'}

    let soundColumns = [
        {title: 'Sound', field: 'NewSound', sortable: false, defaultValue: null, editView: binaryView("ogg", "SoundData")},
        {title: 'SoundType', field: 'SoundType', sortable: true, defaultValue: soundTypes[0], editView: dropdownView(soundTypes)},
    ];
</script>

{#if selectedValue}
    <div class="inline fixed top-0 right-0 left-0 bottom-0 bg-black opacity-25 z-10"></div>
    <div tabindex="-1" aria-hidden="true" class="fixed top-0 left-0 right-0 z-50 w-full p-4 overflow-auto md:inset-0 h-[calc(100%-1rem)] max-h-full" style="min-width: max-content;">
        <div class="relative w-full max-h-full">
            <div class="relative bg-white rounded-lg shadow">
                <div class="flex items-start justify-between p-4 border-b rounded-t">
                    <h3 class="text-xl font-semibold text-gray-900">
                        <input bind:value="{selectedValue.Name}" />
                        <label>{oldName}</label>
                        {#if !selectedValue.Name }
                            <div class="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
                                <span class="block sm:inline">Name can't be empty.</span>
                            </div>
                        {/if}
                    </h3>
                    <button type="button" class="text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm p-1.5 ml-auto inline-flex items-center" on:click={handleClose}>
                        <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"></path></svg>
                    </button>
                </div>
                <div class="p-6 space-y-6">
                    <div class="grid">
                        <div>
                            <span>{selectedValue.ID}</span>
                            <FbxViewer modelUrl={binaryDataUrl(selectedValue.ModelDataId)}/>
                            <a href={binaryDataUrl(selectedValue.ModelDataId)}>Download model</a>
                        </div>
                        <div class="object-contain">
                            <StraightTable columns={soundColumns} data={sounds} on:change={saveSounds} settings={{canResetRows:false}}></StraightTable>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
{/if}
{#if props.length}
    <div class="min-w-full">
<table class="table">
    <thead class="sticky bg-white">
    <tr>
        <td class="pt-2" colspan="16">
            <button
                class="btn-primary"
                disabled={Object.keys(diff).length <= 0}
                on:click={uploadProps}>Save changes
            </button>
            <div class="float-right">
                <input bind:value={searchValue} placeholder="Search" type="text"/>

                <div class="inline-flex rounded-md shadow-sm" role="group">
                    <button
                        class="py-2 px-4 text-sm font-medium text-gray-900 {filterType === filterTypes.all ? 'bg-yellow-300 hover:bg-yellow-500' : 'bg-white hover:bg-gray-100'}  rounded-l-lg border border-gray-200"
                        on:click={() => setFilterType(filterTypes.all)} type="button">
                        All
                    </button>
                    <button
                        class="py-2 px-4 text-sm font-medium text-gray-900 {filterType === filterTypes.last24Hours ? 'bg-yellow-300 hover:bg-yellow-500' : 'bg-white hover:bg-gray-100'} rounded-r-md border border-gray-200"
                        on:click={() => setFilterType(filterTypes.last24Hours)} type="button">
                        Last 24h
                    </button>
                </div>
            </div>
        </td>
    </tr>
    <tr>
        <th>
            <input bind:this={multiEditCheckbox} class="pr-2" on:click={toggleMultiEditMode} type="checkbox"/>
        </th>
        <th class="cursor-pointer" on:click={()=>sortByColumn("Name")}>Name
            {#if sortBy.column === "Name"}{@html getArrow(sortBy) }{/if}
        </th>
        <th class="cursor-pointer" on:click={()=>sortByColumn("Bundle")}>Bundle{#if sortBy.column === "Bundle"}{@html getArrow(sortBy) }{/if}</th>
        <th>Tags</th>
        <th>Width</th>
        <th>Height</th>
        <th>Tallness</th>
        <th title="Snap To grid">STG</th>
        <th title="Permanent">Pmt</th>
        <th title="Do Not Block Tracks">DNBT</th>
        <th title="Area Multiplier">AM</th>
        <th>Destroy Fee</th>
        <th class="cursor-pointer" on:click={()=>sortByColumn("InternalKey")}>InternalKey
            {#if sortBy.column === "InternalKey"}{@html getArrow(sortBy) }{/if}
        </th>
        <th class="cursor-pointer" on:click={()=>sortByColumn("Collection")}>Collection
            {#if sortBy.column === "Collection"}{@html getArrow(sortBy) }{/if}
        </th>
        <th class="cursor-pointer" on:click={()=>sortByColumn("UpdatedAt")}>Updated At
            {#if sortBy.column === "UpdatedAt"}{@html getArrow(sortBy) }{/if}
        </th>
        <th>
            ID
        </th>
        <th class="cursor-pointer" on:click={()=>sortByColumn("NameOverride")}>Name Override
            {#if sortBy.column === "NameOverride"}{@html getArrow(sortBy) }{/if}
        </th>
    </tr>
    </thead>
    <tbody class="border-b">
    {#each filteredRows as row, i}
        <tr class="border-b striped {highlightClass(row, multiEditIndexes, diff)} transition-colors py-2">
            <td on:click={() => toggleMultiEditRow(row)}>
                <input type="checkbox" checked={multiEditIndexes[row.ID]}/>
            </td>
            <td class="cursor-pointer whitespace-nowrap" on:click={() => selectRow(row)}>{row.Name}</td>
            <td>{row.Bundle}</td>
            <td>{row.TagsString}</td>
            <td>
                <input
                    class="text-input w-16"
                    type="number"
                    min="1"
                    bind:value={row.Width}
                    on:input={() => multiEdit("Width", row)}
                />
            </td>
            <td>
                <input
                    class="text-input w-16"
                    type="number"
                    min="1"
                    bind:value={row.Height}
                    on:input={() => multiEdit("Height", row)}
                />
            </td>
            <td>
                <input
                    class="text-input w-16"
                    type="number"
                    min="1"
                    bind:value={row.Tallness}
                    on:input={() => multiEdit("Tallness", row)}
                />
            </td>
            <td>
                <input
                    class={checkboxClass}
                    type="checkbox"
                    bind:checked={row.SnapToGrid}
                    on:change={() => multiEdit("SnapToGrid", row)}
                />
            </td>
            <td>
                <input
                    class={checkboxClass}
                    type="checkbox"
                    bind:checked={row.Permanent}
                    on:change={() => multiEdit("Permanent", row)}
                />
            </td>
            <td>
                <input
                    class={checkboxClass}
                    type="checkbox"
                    bind:checked={row.DoNotBlockTracks}
                    on:change={() => multiEdit("DoNotBlockTracks", row)}
                />
            </td>
            <td>
                <input
                    class="text-input w-14"
                    type="number"
                    bind:value={row.AreaMultiplier}
                    on:input={() => multiEdit("AreaMultiplier", row)}
                />
            </td>
            <td>
                <input
                    class="text-input w-28"
                    type="number"
                    bind:value={row.DestroyFee}
                    on:input={() => multiEdit("DestroyFee", row)}
                />
            </td>

            <td>
                <input
                    class="text-input"
                    type="text"
                    bind:value={row.InternalKey}
                    on:input={() => multiEdit("InternalKey", row)}
                />
            </td>
            <td>
                <input
                    class="text-input"
                    type="text"
                    bind:value={row.Collection}
                    on:input={() => multiEdit("Collection", row)}
                />
            </td>
            <td class="whitespace-nowrap">
                {dateFormatter.format(row.UpdatedAt)}
            </td>
            <td>{row.ID}</td>
            <td>{row.NameOverride}</td>
        </tr>
    {/each}
    </tbody>
</table>
</div>
{:else}
    <span class="animate-pulse">Loading...</span>
{/if}
<svelte:window on:keydown={onKeyDown} on:keyup={onKeyUp}/>

<style>
    tr.changed, tr.striped.changed {
        @apply bg-blue-200
    }
    th, td {
        @apply px-1 py-1
    }
</style>
